import React, { useState } from 'react';
import { getDoc } from 'firebase/firestore';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { auth, db, collection, addDoc, serverTimestamp, doc, updateDoc, query, where, getDocs, setDoc } from '../firebase';
import './reflectionForm.css';
import '../UserProfile.css';

export default function ReflectionForm({ selectedEmotionProp, isSideBySide, dateStrProp, moodTypeProp }) {
    const navigate = useNavigate();
    const location = useLocation();

    const existingData = location.state?.existingData;
    const formattedDate = location.state?.formattedDate;
    const isExisting = !!existingData;
    const isSubmittedNow = location.state?.isSubmittedNow;

    const [isSubmittedLocal, setIsSubmittedLocal] = useState(isSubmittedNow || false);
    const [submittedTime, setSubmittedTime] = useState(isSubmittedNow ? new Date() : null);
    const displayAsView = isExisting || isSubmittedLocal;

    const emotion = selectedEmotionProp || (existingData?.emotion) || (location.state?.emotion) || '...'; // fallback
    const passedDateStr = dateStrProp || location.state?.dateStr;

    const passedMoodType = moodTypeProp || location.state?.moodType || (existingData?.moodType);

    let formRows = [];
    if (passedMoodType === 'positive' || passedMoodType === 'I had a good day!') {
        formRows = [
            { label: "How I feel in my body/My behaviour is", placeholder: "e.g., cool, calm, energetic, productive..." },
            { label: "My thoughts are", placeholder: "e.g., I did a great job today, I feel at peace..." },
            { label: "What situation could be contributing to this feeling ?", placeholder: "e.g., A good conversation, achieving a goal, enjoying a hobby..." }
        ];
    } else {
        formRows = [
            { label: "How I feel in my body/My behaviour is", placeholder: "e.g., withdrawn, reactive, quiet, restless..." },
            { label: "My thoughts are", placeholder: "e.g., I'm overwhelmed, this is too hard..." },
            { label: "What situation could be contributing to this feeling ?", placeholder: "e.g., A stressful event at work, lack of sleep, an argument..." },
            { label: "I need", placeholder: "e.g., Some time alone, to talk to someone, a break..." },
            { label: "I would like if", placeholder: "e.g., People would listen more, I had more time to rest..." }
        ];
    }

    const [expandedRow, setExpandedRow] = useState(null);
    const [answers, setAnswers] = useState(() => {
        if (existingData) {
            return {
                0: existingData.behaviour || "",
                1: existingData.thoughts || "",
                2: existingData.situations || "",
                3: existingData.need || "",
                4: existingData.makes || ""
            };
        }
        return {};
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const [summary, setSummary] = useState("");
    const [loadingSummary, setLoadingSummary] = useState(false);

    const context = useOutletContext();
    const user = context?.user;
    const setIsLoginOpen = context?.setIsLoginOpen;

    React.useEffect(() => {
        if (user && pendingSubmit) {
            setPendingSubmit(false);
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pendingSubmit]);

    React.useEffect(() => {
        const currentUser = user || auth.currentUser;
        if (displayAsView && currentUser) {
            const fetchOrGenerateSummary = async () => {
                setLoadingSummary(true);
                const dateStr = passedDateStr ? passedDateStr : (existingData?.entry_time ? existingData.entry_time.split('T')[0].split(' ')[0] : new Date().toISOString().split('T')[0]);

                try {
                    const summaryDocRef = doc(db, "journal_summaries", `${currentUser.email}_${dateStr}`);
                    const summarySnap = await getDoc(summaryDocRef);

                    if (summarySnap.exists()) {
                        setSummary(summarySnap.data().summary);
                    } else {
                        // Fetch entries
                        const q = query(
                            collection(db, "journals"),
                            where("email", "==", currentUser.email),
                            where("Date", "==", dateStr)
                        );
                        const querySnapshot = await getDocs(q);
                        const entries = [];
                        querySnapshot.forEach((d) => {
                            entries.push(d.data());
                        });

                        if (entries.length > 0) {
                            const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
                            if (apiKey) {
                                let prompt = `Here are my journal entries for today:\n\n`;
                                entries.forEach((entry, index) => {
                                    prompt += `Entry ${index + 1}:\n- Emotion: ${entry.emotion}\n- Behavior: ${entry.behaviour}\n- Thoughts: ${entry.thoughts}\n- Situation: ${entry.situations}\n- Need: ${entry.need}\n- Would like if: ${entry.makes}\n`;
                                    if (entry.other) {
                                        prompt += `- Other Context: ${entry.other}\n`;
                                    }
                                    prompt += `\n`;
                                });
                                prompt += `Please provide a short, empathetic, and highly motivating summary of my day, focusing on my emotions, behaviors, and what made me feel that way. Address me as "You". Ensure the tone is encouraging, supportive, and uplifts me, acknowledging the complexity of feeling multiple emotions if present. Keep it under 2-3 sentences so it generates quickly. Do not use markdown headers, just return a plain text or simple markdown summary.

CRITICAL INSTRUCTION FOR SENSITIVE CONTENT:
If the user expresses deep distress, uncontrollable negative emotions (e.g., extreme anger, despair, "want to kill"), or severe situations:
DO NOT refuse to answer or output a generic safety block message. Instead, acknowledge their pain with profound empathy and provide a gentle, supportive response encouraging them to seek professional help.
Example format for sensitive input: "It sounds like you are experiencing overwhelming emotions right now, and I can see how deeply this situation is affecting you. I may not fully understand the depth of what you're going through, but please know that these intense feelings are a sign that you might need more support than a reflection can offer. Please feel free to reach out to a psychologist or professional who can help you navigate this difficult time."

CRITICAL INSTRUCTION FOR ALL OTHER CONTENT: Based on the combination of my emotions and actions/behaviors, you MUST append one of the following exact reinforcing statements to the end of the summary:
- If I had a positive emotion and positive actions: "Thank you for sharing with us that you are feeling [Emotion] today, continue to keep doing what you are doing! Every time you are [Emotion], you're making a difference in your life and spreading that to other people."
- If I had a positive emotion but my actions/behaviors seem negative or contradictory: "I'm sorry we don't quite understand this situation, happiness is always healthy and it's an outcome of fulfilling and meaningful actions towards oneself and others. The feeling of [Emotion] often feels more lasting when they come from constructive actions."
- If I had a negative emotion and positive actions: "It takes immense strength to act positively when you feel down. Your resilience is inspiring and you are building a stronger version of yourself."
- If I had a negative emotion and negative actions: Provide a personalized, deeply comforting and motivating statement reminding me that it is okay to feel this way and gently encouraging resilience.`;

                                const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent?key=${apiKey}`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
                                });
                                const data = await response.json();
                                const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

                                if (generatedText) {
                                    setSummary(generatedText);
                                    await setDoc(summaryDocRef, {
                                        email: currentUser.email,
                                        date: dateStr,
                                        summary: generatedText,
                                        createdAt: serverTimestamp()
                                    });
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.error("Error fetching or generating summary:", err);
                } finally {
                    setLoadingSummary(false);
                }
            };
            fetchOrGenerateSummary();
        }
    }, [displayAsView, user, auth.currentUser, passedDateStr, existingData]);

    const handleSubmit = async () => {
        const currentUser = user || auth.currentUser;
        if (!currentUser) {
            if (setIsLoginOpen) setIsLoginOpen(true);
            setPendingSubmit(true);
            return;
        }

        // Validate that all questions have been answered
        const requiredIndices = (passedMoodType === 'positive' || passedMoodType === 'I had a good day!') ? [0, 1, 2] : [0, 1, 2, 3, 4];
        const allAnswered = requiredIndices.every(index => answers[index] && answers[index].trim() !== "");
        if (!allAnswered) {
            alert("Please answer all questions before submitting your reflection.");
            return;
        }

        setIsSubmitting(true);

        const finalDateToUse = passedDateStr ? passedDateStr : new Date().toISOString().split('T')[0];

        const payload = {
            email: currentUser.email,
            emotion: emotion,
            moodType: passedMoodType || 'positive',
            other: location.state?.otherText || existingData?.other || "",
            behaviour: answers[0] || "",
            thoughts: answers[1] || "",
            situations: answers[2] || "",
            need: answers[3] || "",
            makes: answers[4] || "", // mapped "I would like if" to makes
            entry_time: finalDateToUse,
            Date: finalDateToUse
        };

        console.log(payload, "selfrefection creating journal paylaod")
        try {
            await addDoc(collection(db, "journals"), {
                ...payload,
                createdAt: serverTimestamp()
            });

            // Update the user's lastJournaledAt timestamp
            try {
                const q = query(collection(db, "users"), where("uid", "==", currentUser.uid));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const userDoc = querySnapshot.docs[0];
                    await updateDoc(doc(db, "users", userDoc.id), {
                        lastJournaledAt: serverTimestamp()
                    });
                }
            } catch (err) {
                console.error("Error updating user lastJournaledAt:", err);
            }

            alert("Journal created successfully!");

            if (isSideBySide) {
                navigate("/reflection-form", {
                    state: {
                        existingData: payload,
                        formattedDate: formattedDate || finalDateToUse,
                        isSubmittedNow: true
                    }
                });
            } else {
                setIsSubmittedLocal(true);
                setSubmittedTime(new Date());
            }
        } catch (error) {
            console.error("Firestore Error:", error);
            alert("Error saving journal: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={isSideBySide ? "rf-container-sidebyside" : "rf-container"}>
            <div className="rf-content" style={(isSideBySide || displayAsView) ? { padding: '40px 20px', maxWidth: '100%' } : {}}>
                {!isSideBySide && (
                    <>
                        {!displayAsView && (
                            <div className="rf-title-section">
                                <div className="rf-sun-bg"></div>
                                <img
                                    src="/reflections/nabilogo.png"
                                    alt="Wings Logo"
                                    className="rf-logo-img"
                                />
                            </div>
                        )}

                        <div className={displayAsView ? "rf-header-row-view" : "rf-header-row"}>
                            <div /> {/* Spacer for back button removed */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 10px' }}>
                                <h1 className="rf-title" style={{ fontSize: displayAsView ? '1.4rem' : '1.6rem', lineHeight: '1.4' }}>
                                    {displayAsView ? (
                                        <>
                                            Hi {user?.displayName || auth?.currentUser?.displayName || (user?.email ? user.email.split('@')[0] : 'User')}! Look through your journal entry for today
                                            <br />
                                            {formattedDate || "this day"}
                                        </>
                                    ) : (
                                        formattedDate || "My Reflections"
                                    )}
                                </h1>
                                {submittedTime && (
                                    <span style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                                        Submitted at {submittedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                )}
                            </div>
                            <div /> {/* Spacer for grid alignment */}
                        </div>
                    </>
                )}

                {displayAsView ? (
                    <div className="rf-view-container">
                        <div className="rf-view-layout-wrapper">
                            {/* Summary Display First (Left) */}
                            <div className="rf-view-left-panel">
                                {loadingSummary && !summary && <p style={{ textAlign: 'center', marginTop: '30px', color: '#A46D5B', fontSize: '1.2rem', fontWeight: '500', animation: 'es-fade-in 1s infinite alternate' }}>Generating Summary of todays journal...</p>}

                                {summary && (
                                    <>
                                        <div className="journal-summary-container" style={{ backgroundImage: "url('/mountains.png')", marginTop: '0', width: '100%' }}>
                                            <h3 style={{ textAlign: 'center', alignSelf: 'center', width: '100%', color: '#A46D5B', fontFamily: 'Syne, sans-serif', fontSize: '1.5rem', fontWeight: '600', marginTop: '0', marginBottom: '25px' }}>
                                                A Moment of Understanding
                                            </h3>
                                            <div className="journal-summary-flex">
                                                <div className="journal-avatar">
                                                    <img
                                                        src={`/emotions/${((emotion || "happy").split(',')[0]).toLowerCase().replace(/\s+/g, '')}.png`}
                                                        alt="Avatar"
                                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </div>
                                                <div className="journal-cloud-bubble">
                                                    {summary}
                                                </div>
                                            </div>
                                        </div>
                                        {passedMoodType !== 'positive' && passedMoodType !== 'I had a good day!' && (
                                            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '14px', color: '#555', lineHeight: '1.5', fontFamily: 'Inter, sans-serif' }}>
                                                Hope reflecting helped you understand your feelings. If you’re still feeling unsettled, you can connect with our psychologist.<span onClick={() => navigate('/therapist')} style={{ color: '#003049', textDecoration: 'underline', cursor: 'pointer', fontWeight: '600' }}> Your first conversation is on us.</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Form Answers Beside It (Right) */}
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '20px', flex: '1 1 500px', maxWidth: '600px', width: '100%' }}>
                                <div className="rf-view-right-panel" style={{ flex: 'unset', maxWidth: '100%', width: '100%' }}>
                                    {emotion !== "Words can't describe" && (
                                        <div className="rf-view-emotion" style={{ marginTop: '0' }}>{emotion}</div>
                                    )}

                                <div className="rf-view-answers" style={{ width: '100%' }}>
                                    {formRows.map((row, index) => {
                                        const answerText = answers[index];
                                        if (!answerText || answerText.trim() === "") return null;

                                        let displayLabel = row.label;
                                        if (index === 0) displayLabel = "In this moment Ifeel in my body/My behaviour is..";
                                        if (index === 4) displayLabel = "I would like if...";

                                        return (
                                            <div key={index} className="rf-view-qa-block">
                                                <div className="rf-view-question">{displayLabel}</div>
                                                <div className="rf-view-answer-card">
                                                    {answerText}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                </div>
                                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button 
                                        onClick={() => navigate('/selfreflection')} 
                                        className="rf-close-btn"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rf-list formsection">
                        {/* First static row with emotion */}
                        <div className="rf-list-row-header rf-row-bg-1" style={{ padding: '22px 25px' }}>
                            <span className="rf-row-label">I feel</span>
                            <div className="rf-emotion-pill">
                                {emotion}
                            </div>
                        </div>

                        {/* Dynamic rows for the rest */}
                        {formRows.map((row, index) => {
                            const bgClass = `rf-row-bg-${index + 2}`;
                            const isExpanded = expandedRow === index;
                            const isAnswered = answers[index] && answers[index].trim() !== "";

                            return (
                                <div key={index} className={`rf-list-row-container ${bgClass} ${isExpanded ? 'rf-expanded' : ''}`}>
                                    <div
                                        className="rf-list-row-header"
                                        onClick={() => setExpandedRow(isExpanded ? null : index)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className="rf-row-label">{row.label}</span>
                                        <button className="rf-action-btn">
                                            {isAnswered && !isExpanded ? (
                                                <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.5 8.5L4.5 12.5L15.5 1.5" stroke="url(#paint0_linear_btn)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    <defs>
                                                        <linearGradient id="paint0_linear_btn" x1="14.1" y1="1.5" x2="1.68668" y2="12.0325" gradientUnits="userSpaceOnUse">
                                                            <stop stopColor="#FF774C" />
                                                            <stop offset="1" stopColor="#FFE6A7" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            ) : (
                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M13.5 8.5L16.0858 5.91421C16.8668 5.13317 16.8668 3.86683 16.0858 3.08579L15.9142 2.91421C15.1332 2.13316 13.8668 2.13316 13.0858 2.91421L10.5 5.5L2.91421 13.0858C2.13317 13.8668 2.13317 15.1332 2.91421 15.9142L3.08579 16.0858C3.86684 16.8668 5.13316 16.8668 5.91421 16.0858L13.5 8.5ZM10.5 5.5L13.5 8.5M5.5 0.5V3.5M4 2H7M15.5 12.5V14.5M15.5 14.5V16.5M15.5 14.5H13.5M15.5 14.5H17.5M1.5 6.5V8.5M0.5 7.5H2.5" stroke="#09090B" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="rf-textarea-container">
                                            <textarea
                                                className="rf-textarea"
                                                value={answers[index] || ""}
                                                onChange={(e) => setAnswers({ ...answers, [index]: e.target.value })}
                                                placeholder={displayAsView ? "" : (row.placeholder || "Type your answer here...")}
                                                readOnly={displayAsView}
                                                autoFocus={!displayAsView}
                                            />
                                            <button
                                                className="rf-save-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedRow(null);
                                                }}
                                            >
                                                <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.5 8.5L4.5 12.5L15.5 1.5" stroke="url(#paint0_linear_166_6607)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    <defs>
                                                        <linearGradient id="paint0_linear_166_6607" x1="14.1" y1="1.5" x2="1.68668" y2="12.0325" gradientUnits="userSpaceOnUse">
                                                            <stop stopColor="#FF774C" />
                                                            <stop offset="1" stopColor="#FFE6A7" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="rf-footer-action">
                    {!displayAsView && (
                        <button className="rf-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
